<template>
  <!-- Kontener strony logowania -->
  <div :class="['container', { 'container--sg': shouldHideLocation }]">
    <div class="card m-3 text-center">
      <!-- Nagłówek -->
      <h4 class="card-header">
        {{ isSsoProcessing ? 'Logowanie...' : t('log-in') }}
      </h4>

      <div class="d-flex card-body justify-content-center">
        <!-- 🔄 Widok SSO -->
        <div
            v-if="isSsoProcessing"
            class="d-flex flex-column align-items-center py-4"
        >
          <div class="spinner-border mb-3" role="status">
            <span class="visually-hidden">{{ t('logging-in') }}</span>
          </div>
          <div>{{ t('logging-in') }}</div>
        </div>

        <!-- 🧾 Zwykły formularz logowania -->
        <Form
            v-else
            @submit="onSubmit"
            :validation-schema="schema"
            v-slot="{ errors, isSubmitting, values }"
        >
          <!-- Pole loginu -->
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('login') }}</label>
            <div class="col-sm-10">
              <Field
                  name="username"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.username }"
              />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>
          </div>

          <!-- Pole hasła -->
          <div class="form-group row mb-1">
            <label class="col-sm-2 col-form-label">{{ t('password') }}</label>
            <div class="col-sm-10">
              <Field
                  name="password"
                  type="password"
                  class="form-control"
                  :class="{ 'is-invalid': errors.password }"
              />
              <div class="invalid-feedback">{{ errors.password }}</div>
            </div>
          </div>

          <!-- Link do resetu hasła, przycisk logowania -->
          <div class="form-group row d-flex flex-column align-items-center">
            <div class="col-auto">
              <router-link to="reset-password" class="btn btn-link">
                {{ t('forgot-password') }}
              </router-link>
            </div>
            <div v-if="isSG" class="col-auto">
              <a href="/downloads/mkcert-root-ca.crt" target="_blank" rel="noopener" class="download-link">
                {{ t('download-certificate') }}
              </a>
            </div>
            <div v-if="isSG" class="col-auto">
              <a href="/downloads/instrukcja.pdf" target="_blank" rel="noopener" class="download-link">
                {{ t('download-manual') }}
              </a>
            </div>
            <div class="col-auto">
              <button class="btn btn-outline-secondary" :disabled="isSubmitting">
                <span
                    v-show="isSubmitting"
                    class="spinner-border spinner-border-sm mr-1"
                ></span>
                {{ t('log-in').toUpperCase() }}
              </button>
            </div>
          </div>

          <!-- Pozostawiona sesja: tylko po odmowie „już zalogowany" dla tego samego loginu. -->
          <div
              v-if="authStore.pendingForceLogout && authStore.pendingForceLogout.username === values.username"
              class="force-logout"
          >
            <p class="force-logout__hint">{{ t("force-logout-hint") }}</p>
            <button
                type="button"
                class="btn btn-outline-secondary"
                :disabled="isForceLoggingOut"
                @click="onForceLogout"
            >
              <span
                  v-show="isForceLoggingOut"
                  class="spinner-border spinner-border-sm mr-1"
              ></span>
              {{ t("force-logout") }}
            </button>
          </div>

          <!-- Błąd logowania trafia do globalnego powiadomienia (AppNotifications). -->
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useAuthStore } from "@/stores/auth.store";
import { useUserStore } from "@/stores/user.store";
import { useNotificationStore } from "@/stores/notification.store";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { isSG } from "@/config/appVersion";

const { t } = useI18n();

const notify = useNotificationStore();
const authStore = useAuthStore();
const router = useRouter();

// 🔄 czy trwa logowanie SSO
// sprawdzamy to OD RAZU, zanim komponent się wyrenderuje
const searchNow = window.location.search || "";
const paramsNow = new URLSearchParams(searchNow);
const initialTicket =
    paramsNow.get("ticket") || sessionStorage.getItem("desktopSsoTicket");

const isSsoProcessing = ref(!!initialTicket);

// schemat walidacji
const schema = Yup.object().shape({
  username: Yup.string().required(t("user-login-required")),
  password: Yup.string().required(t("user-password-required")),
});

// zwykły submit formularza
async function onSubmit(values) {
  const { username, password } = values;
  await authStore.login(username, password);
}

// „Wyloguj mnie" — zamyka pozostawioną sesję konta (np. po zamknięciu komputera bez wylogowania)
const isForceLoggingOut = ref(false);

async function onForceLogout() {
  isForceLoggingOut.value = true;
  try {
    await authStore.forceLogout();
  } finally {
    isForceLoggingOut.value = false;
  }
}

// ---- SSO: automatyczne logowanie po ?ticket=... ----
onMounted(async () => {
  const ticket = initialTicket;

  if (!ticket) {
    // brak biletu – upewnij się, że spinner nie wisi
    isSsoProcessing.value = false;
    return;
  }

  try {
    const res = await fetchWrapper.post(
        `${process.env.VUE_APP_API_URL}/desktop-sso-exchange`,
        { ticket }
    );

    // bilet zużyty – od razu wyczyść
    sessionStorage.removeItem("desktopSsoTicket");

    authStore.token = res.token;
    sessionStorage.setItem("cvToken", res.token);
    const passwordExpired = await authStore.ensurePasswordFreshness();
    if (passwordExpired) {
      notify.warning(t("password-expired-set-new"));
      await router.push("/password-expired");
      return;
    }

    authStore.startInactivityWatch();
    authStore.ping(false);

    // Załaduj profil i flagi funkcji zanim wybierzemy trasę — `landingPath()` używa ról,
    // a menu buduje się z flag już przy pierwszym renderze layoutu.
    const userStore = useUserStore();
    await authStore.prepareAppContext(userStore);
    const target = authStore.resolvePostLoginTarget(userStore);

    await router.push(target);
  } catch (e) {
    console.error("[SSO] exchange error", e);
    notify.error(t("sso-login-failed"));
  } finally {
    // jeśli SSO padło albo skończyło się, wróć do normalnego formularza
    isSsoProcessing.value = false;
  }
});

const shouldHideLocation = computed(() => isSG);
</script>

<style scoped>
/* Kontener z tłem oraz responsywnym środkiem */
.container {
  background-image: url("../assets/login.jpg"); /* obrazek w tle */
  width: 100%;
  min-height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(var(--spacing-2xl), 12vh, 150px) var(--spacing-lg) var(--spacing-2xl);
  position: relative;
}

.container--sg {
  background-image: url("../assets/sglogin.jpg"); /* obrazek w tle dla wersji SG */
}

/* Półprzezroczysta warstwa nakładki dla lepszej czytelności formularza */
.container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(241, 241, 241, 0.5);
  z-index: 1;
}

/* Zawartość na wierzchu nakładki */
.container > * {
  position: relative;
  z-index: 2;
}

.card {
  width: 100%;
  max-width: 500px;
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
}

.card-body {
  background: var(--surface-primary);
  padding: 1rem;
}

.card-body form {
  width: 400px;
}

.col-form-label {
  text-align: right;
  line-height: 25px;
}

/* Stylowanie przycisków i linków */
button {
  border: 0;
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}
a {
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  text-decoration: none;
}

.btn-outline-secondary {
  color: var(--text-secondary);
  border-color: var(--text-secondary);
  margin-top: 0.5rem;
}

.btn-outline-secondary:hover {
  color: var(--text-inverse);
  background-color: var(--text-secondary);
  border-color: var(--text-secondary);
}

.btn-outline-secondary:focus,
.btn-outline-secondary.focus {
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5);
}

.btn-outline-secondary.disabled,
.btn-outline-secondary:disabled {
  color: var(--text-secondary);
  background-color: transparent;
}

.btn-outline-secondary:not(:disabled):not(.disabled):active,
.btn-outline-secondary:not(:disabled):not(.disabled).active,
.show > .btn-outline-secondary.dropdown-toggle {
  color: var(--text-inverse);
  background-color: var(--text-secondary);
  border-color: var(--text-secondary);
}

.btn-outline-secondary:not(:disabled):not(.disabled):active:focus,
.btn-outline-secondary:not(:disabled):not(.disabled).active:focus,
.show > .btn-outline-secondary.dropdown-toggle:focus {
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5);
}

/* Oferta zamknięcia pozostawionej sesji */
.force-logout {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--border-default);
}

.force-logout__hint {
  margin-bottom: 0;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

/* Pola input */
input {
  border: 0;
  border-left: 1px solid var(--border-default);
  border-top: 1px solid var(--border-default);
  padding: 10px;
}
input:focus {
  border-color: var(--border-default);
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
</style>
